import React, { useMemo } from 'react';
import * as WidgetBlocks from '@metservice/mov-report-view-blocks';
import { providers } from '@metservice/mov-report-view-blocks';
import { weatherWidgets } from '@metservice/platforms-weather-widgets';
import { DateTime } from 'luxon';
import { transformTimeRangeToDurations } from '@metservice/mov-report-view-blocks/src/utils/timeRangeMagic';

const { useWidgetBlockReducer, redux } = providers;

import LoadingOverlay from './LoadingOverlay';
import ReportStyledWidgetWrapper from './ReportStyledWidgetWrapper';
import ReportPreviewFallback from './ReportPreviewFallback';

const WidgetBlockContainer = ({
    widgetblockType,
    viewblockState,
    date,
    timezone,
    updateInterval,
    stage,
    renderingForPDF,
    cfjsons,
    datasets,
    theme,
    isLoading,
    metjwt,
}) => {
    const timezoneToUse = timezone && timezone !== 'Delegate' ? timezone : viewblockState.timezone;
    const weatherWidget = weatherWidgets.find((w) => w.type === widgetblockType);
    if (weatherWidget) {
        return (
            <weatherWidget.Render
                stage={stage ?? 'prod'}
                settings={{ ...viewblockState, timezone: timezoneToUse, isInteractive: false }}
                isRenderingForPDF={renderingForPDF}
                cfjsons={cfjsons}
                datasets={datasets}
                theme={theme}
                isLoading={isLoading}
                metjwt={metjwt}
            />
        );
    }

    const widgetBlock = WidgetBlocks[widgetblockType];
    const WidgetComponent = widgetBlock.widgetComponent;

    const [oldReduxState, dispatch] = useWidgetBlockReducer({
        viewblockState,
        widgetBlock,
        date,
        timezone: timezoneToUse,
        updateInterval,
        datasets: datasets,
        cfjsons: cfjsons,
    });

    const reduxState = useMemo(() => (oldReduxState && oldReduxState.toJS ? oldReduxState.toJS() : oldReduxState), [oldReduxState]);

    const widget = useMemo(() => {
        if (!reduxState) return null;

        return (
            <WidgetComponent
                widgetModel={reduxState.model}
                error={reduxState.error}
                fireNotificationDismissed={(notificationId) => dispatch(redux.notificationDismissed(null, null, notificationId))}
                fireCustomEvent={(type, payload) => dispatch(redux.customEvent(null, null, type, payload))}
                isLoading={isLoading}
            />
        );
    }, [oldReduxState, isLoading]);

    return (
        <ReportStyledWidgetWrapper>
            <LoadingOverlay loading={isLoading}>{widget}</LoadingOverlay>
        </ReportStyledWidgetWrapper>
    );
};

export default ({ widgetblockType, viewblockState, date, timezone, updateInterval, stage, renderingForPDF, cfjsons, datasets, isLoading, metjwt }) => (
    <ReportPreviewFallback widgetblockName={widgetblockType}>
        <WidgetBlockContainer
            widgetblockType={widgetblockType}
            viewblockState={viewblockState}
            date={date}
            timezone={timezone}
            updateInterval={updateInterval}
            stage={stage}
            renderingForPDF={renderingForPDF}
            cfjsons={cfjsons}
            datasets={datasets}
            isLoading={isLoading}
            metjwt={metjwt}
        />
    </ReportPreviewFallback>
);
